/**
 * El cliente HTTP, tipado contra @tennisup/contracts.
 *
 * Tres reglas que valen para toda la app:
 *
 *  1. Los tipos de la petición y de la respuesta salen del contrato. Si la
 *     API cambia un campo, esto deja de compilar acá y no falla en runtime
 *     en un formulario que nadie tocó.
 *
 *  2. El `message` del servidor NUNCA se muestra. Viene en inglés y está
 *     escrito para un log. Lo que se muestra sale del `code`, traducido en
 *     el catálogo del idioma que la persona eligió.
 *
 *  3. El token de sesión se pone UNA vez, acá, y viaja solo. Si cada
 *     pantalla tuviera que acordarse de mandarlo, la que se olvide falla con
 *     un 401 que parece un problema de sesión y es un descuido.
 */
import type {
  AskForRankingRequest,
  ReviewRankingRequest,
  AcceptAdminInvitationResponse,
  ChallengePost,
  Novelties,
  ChallengePostContact,
  ChallengePostPage,
  Court,
  CourtPage,
  CourtQuery,
  SaveChallengePostRequest,
  SaveCourtRequest,
  AdminInvitation,
  AdminInvitationPreview,
  AddCommunityMemberRequest,
  ApiErrorBody,
  AuthResponse,
  AuthUser,
  AnswerChallengeRequest,
  Challenge,
  EntryPaymentMethod,
  ReviewEntryRequest,
  ChallengePlayer,
  CreateChallengeRequest,
  SetChallengeResultRequest,
  ReportFriendlyRequest,
  ChallengeSearchRequest,
  ChangePasswordRequest,
  CategoryDiscipline,
  CategoryLevel,
  CategorySex,
  Community,
  CommunityAdmin,
  CommunityCategory,
  CommunityMember,
  CommunityTournamentsSummary,
  CreateCommunityFields,
  JoinRequest,
  LoginRequest,
  PublicTournamentCard,
  Sex,
  PlatformFeatures,
  PlayedMatch,
  PlayerProfile,
  PlayerSearchPage,
  PublicPlayer,
  PublicTournament,
  Rivalry,
  AddAccountRequest,
  AddCommentRequest,
  MatchComment,
  SetReactionRequest,
  ActivityEntry,
  ActivityPage,
  ActivityQuery,
  WallEntry,
  WallReactionCount,
  TournamentHistoryRow,
  UpcomingMatch,
  RegisterRequest,
  RefreshRequest,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  ReviewCommunityRequest,
  SetTournamentPermissionRequest,
  UpdateCommunityDetailsRequest,
  UpdateCommunityMemberRequest,
  SetEmergencyRequest,
  SetPreferencesRequest,
  UpdateProfileRequest,
} from '@tennisup/contracts';
import type {
  CategoryBracket,
  CreateTournamentFields,
  UpdateTournamentRequest,
  Match,
  MatchScore,
  NotificationsPage,
  PlatformSettings,
  PlayedUpNotice,
  PlayerPoints,
  SetCategoryFormatRequest,
  DrawRequest,
  Meme,
  PlayerReport,
  TournamentAdmin,
  TournamentNote,
  PlayerReportPage,
  PlayerReportQuery,
  CreatePlayerReportRequest,
  MemePage,
  PointAdjustment,
  SetChallengeSettingsRequest,
  SetMatchSettingsRequest,
  Theme,
  SetMatchScheduleRequest,
  SetRoundDeadlineRequest,
  RankingRow,
  Tournament,
  TournamentBilling,
} from '@tennisup/contracts';
import {
  AVATAR_FIELD,
  COMMUNITY_LOGO_FIELD,
  ENTRY_PROOF_FIELD,
  SPONSOR_IMAGE_FIELD,
  MATCH_PHOTO_FIELD,
  MEME_IMAGE_FIELD,
  PLAYER_REPORT_PHOTO_FIELD,
  TOURNAMENT_BANNER_FIELD,
} from '@tennisup/contracts';

// Incluye /api: la API monta TODO bajo ese prefijo
// (`app.setGlobalPrefix('api')` en apps/api/src/main.ts). Sin él, cada
// llamada da 404 y la app se ve rota entera aunque las dos partes estén
// bien por separado.
const BASE_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3005/api';

/** Códigos propios del cliente, para fallos que el servidor nunca reporta. */
export const CLIENT_ERROR_CODES = ['NETWORK', 'UNKNOWN'] as const;

/**
 * Un fallo de la API con su código estable.
 *
 * `message` queda para el log; la UI usa `code` y `fieldErrors`.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly statusCode: number;
  readonly fieldErrors: Readonly<Record<string, string>> | undefined;

  constructor(code: string, statusCode: number, message: string, fieldErrors?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.fieldErrors = fieldErrors;
  }
}

// -----------------------------------------------------------------------
// La sesión
// -----------------------------------------------------------------------

/**
 * El token vive en un módulo y no en un contexto de React porque quien lo
 * necesita —`fetch`— no está dentro del árbol de React. Lo pone
 * `AuthProvider` al entrar y lo borra al salir; nadie más lo toca.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * QUÉ HACER CUANDO EL SERVIDOR DICE QUE LA SESIÓN NO VALE.
 *
 * Vive acá y no en cada pantalla porque el 401 puede llegar en cualquiera, y
 * treinta pantallas acordándose de manejarlo son veintinueve que se olvidan.
 * Lo que pasaba: el mensaje «tu sesión se venció» aparecía adentro de la
 * pantalla, con la barra de arriba todavía mostrando el nombre y la foto, y
 * la lista cargando para siempre. Quien lo ve no tiene forma de saber que
 * tiene que ir a «Más» a cerrar sesión para poder volver a entrar.
 *
 * Lo registra `AuthProvider`, que es el único que sabe borrar la sesión.
 */
let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/**
 * CÓMO RENOVAR LA SESIÓN CUANDO EL TOKEN DE ACCESO SE VENCIÓ.
 *
 * Lo registra `AuthProvider`, que es el único que sabe dónde está guardada la
 * llave y dónde hay que dejar la sesión nueva. Acá solo se llama.
 *
 * DEVUELVE `true` SI RENOVÓ. Con `false`, el 401 sigue su camino de siempre:
 * se cierra la sesión y se manda a entrar.
 */
let renovarSesión: (() => Promise<boolean>) | null = null;

export function setRenovarSesión(handler: (() => Promise<boolean>) | null): void {
  renovarSesión = handler;
}

/**
 * UNA SOLA RENOVACIÓN EN VUELO, aunque lleguen diez 401 a la vez.
 *
 * POR QUÉ IMPORTA MÁS DE LO QUE PARECE. Al abrir la aplicación salen varias
 * peticiones juntas —el muro, el perfil, las notificaciones— y si el token
 * venció TODAS reciben 401 casi al mismo tiempo. Sin esto, cada una pediría su
 * propia renovación: la primera canjea la llave, y las otras llegan con una
 * llave YA CANJEADA — que el servidor, con toda razón, lee como un robo y
 * responde matando la cadena entera. O sea que la aplicación se sacaría a sí
 * misma, y justo al abrirla.
 *
 * Con la promesa compartida, la primera renueva y las demás esperan ESA misma
 * respuesta.
 */
let renovaciónEnVuelo: Promise<boolean> | null = null;

function renovarUnaSolaVez(): Promise<boolean> {
  if (renovaciónEnVuelo !== null) return renovaciónEnVuelo;
  if (renovarSesión === null) return Promise.resolve(false);

  renovaciónEnVuelo = renovarSesión().finally(() => {
    renovaciónEnVuelo = null;
  });
  return renovaciónEnVuelo;
}

/**
 * La URL completa de una imagen guardada en el servidor.
 *
 * El contrato devuelve RUTAS relativas, no URLs: la API se alcanza por un
 * host distinto en el navegador, en el celular y en producción, y una URL
 * absoluta guardada en la base queda mal en dos de los tres casos, en
 * silencio. Quien sabe cuál es su API es este cliente.
 *
 * SIN RUTA DEVUELVE NULO, Y «SIN RUTA» ES MÁS QUE `null`.
 *
 * El tipo dice `string | null`, pero un tipo es una promesa sobre el
 * contrato, no una garantía sobre lo que llega por la red: un servidor con
 * una versión anterior OMITE el campo, y entonces llega `undefined`. Con la
 * comprobación puesta solo contra `null`, eso armaba la URL
 * `/uploads/undefined` y el navegador dibujaba el ícono de imagen rota con
 * el texto alternativo al lado. Pasó de verdad con la foto de un partido, en
 * el teléfono de Javier, contra una API que todavía no tenía el campo.
 *
 * La cadena vacía va por lo mismo: `/uploads/` no es una imagen.
 */
export function assetUrl(path: string | null | undefined): string | null {
  if (path === null || path === undefined || path === '') return null;
  return `${BASE_URL}/uploads/${path}`;
}

// -----------------------------------------------------------------------

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { code?: unknown }).code === 'string'
  );
}

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Objeto -> JSON. `FormData` se manda tal cual. `undefined` -> sin cuerpo. */
  body?: unknown;
  /** Para poder cancelar. Ver `searchPlayers`. */
  signal?: AbortSignal;
  /**
   * ESTA PETICIÓN ES UN INTENTO DE AUTENTICARSE, no una acción de alguien ya
   * dentro. Entrar, registrarse y las de contraseña olvidada.
   *
   * Sirve para una sola cosa: su 401 significa «esa contraseña no es» y NO
   * «tu sesión se venció». Sin distinguirlas, alguien con la sesión abierta
   * que escribe mal la contraseña en la pantalla de entrar se quedaría sin la
   * sesión que tenía.
   */
  authenticating?: boolean;
}

/** Se lanza cuando la petición se canceló a propósito. Quien llama la ignora. */
export class AbortedError extends Error {
  constructor() {
    super('Request aborted');
    this.name = 'AbortedError';
  }
}

/**
 * UNA PETICIÓN, CON UN SOLO REINTENTO SI LA SESIÓN SE VENCIÓ.
 *
 * Ante un 401 se intenta renovar UNA vez y se repite la petición. Si la
 * renovación no sale, el 401 sigue su camino de siempre —cerrar sesión y
 * mandar a entrar—, que es lo que pasaba antes de que esto existiera.
 *
 * `reintentando` EVITA EL BUCLE: la segunda vuelta ya no intenta renovar. Sin
 * esa marca, un servidor que devuelve 401 a todo dejaría a la aplicación
 * renovando y reintentando para siempre.
 */
async function request<TResponse>(path: string, options: RequestOptions): Promise<TResponse> {
  try {
    return await intentar<TResponse>(path, options, false);
  } catch (error) {
    const esSesiónVencida =
      error instanceof ApiError && error.statusCode === 401 && options.authenticating !== true;
    if (!esSesiónVencida) throw error;

    // `intentar` ya avisó del 401 solo si no había forma de renovar; acá se
    // prueba, y si sale bien la petición se repite con el token nuevo.
    const renovó = await renovarUnaSolaVez();
    if (!renovó) {
      onUnauthorized?.();
      throw error;
    }
    return intentar<TResponse>(path, options, true);
  }
}

async function intentar<TResponse>(
  path: string,
  options: RequestOptions,
  reintentando: boolean,
): Promise<TResponse> {
  const isForm = options.body instanceof FormData;

  const headers: Record<string, string> = { Accept: 'application/json' };
  // Con FormData NO se pone Content-Type a mano: el navegador tiene que
  // ponerlo él para incluir el `boundary` que separa las partes. Escribirlo
  // acá deja al servidor sin poder leer el cuerpo, y el error que da no dice
  // ni remotamente eso.
  if (options.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (accessToken !== null) headers['Authorization'] = `Bearer ${accessToken}`;

  // `body` se arma aparte y se esparce: con `exactOptionalPropertyTypes`,
  // poner `body: undefined` NO es lo mismo que no poner `body`, y `fetch` no
  // acepta lo primero. Un GET tiene que ir sin la propiedad, no con la
  // propiedad en undefined.
  /*
   * `credentials: 'include'` PARA QUE VIAJE LA COOKIE DE SESIÓN.
   *
   * Sin esto, `fetch` no manda cookies y la de sesión no serviría de nada. La
   * cookie existe porque el token vivía solo en `localStorage`, y WebKit borra
   * eso a los 7 días sin abrir el sitio: la sesión no se vencía, se borraba.
   * Ver `session-cookie.ts` en la API.
   *
   * EL HEADER SIGUE MANDANDO cuando hay token en memoria; la cookie es el plan
   * B para cuando `localStorage` amaneció vacío. El servidor prefiere el
   * header, así que tener las dos nunca se contradice.
   */
  const init: RequestInit = { method: options.method, headers, credentials: 'include' };
  if (options.body !== undefined) {
    init.body = isForm ? (options.body as FormData) : JSON.stringify(options.body);
  }
  if (options.signal !== undefined) init.signal = options.signal;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, init);
  } catch (error) {
    // Cancelada a propósito: no es un fallo de red y no tiene que mostrar
    // «no pudimos hablar con el servidor» mientras la persona sigue tecleando.
    if (error instanceof Error && error.name === 'AbortError') throw new AbortedError();
    // El servidor caído, sin red, o CORS. Para quien mira la pantalla es lo
    // mismo: no se pudo hablar con el servidor.
    throw new ApiError('NETWORK', 0, 'Network request failed');
  }

  const text = await response.text();
  /*
   * UN CUERPO VACÍO ES `null`, NO `undefined`. ESTO TUMBÓ UNA PANTALLA.
   *
   * Cuando un endpoint contesta `null` —«todavía no se enfrentaron», «no hay
   * nada»—, Nest no manda el texto «null»: manda un 200 SIN CUERPO. Acá eso
   * se leía como `undefined`, y `return parsed as TResponse` le ponía encima
   * el tipo declarado, que dice `Rivalry | null`. El tipo prometía un nulo y
   * llegaba un indefinido.
   *
   * La pantalla del cara a cara preguntaba `rivalidad === null` para dibujar
   * «todavía no se enfrentaron». Con `undefined` esa pregunta contesta que no,
   * así que seguía de largo y dibujaba la tabla con nada adentro: pantalla en
   * blanco, sin mensaje, justo en el caso más común —dos personas que nunca
   * jugaron—.
   *
   * `null` es además lo que dice JSON: un cuerpo vacío no es «no contestó»,
   * es «contestó que no hay». Y arregla de una vez cualquier otro endpoint
   * que devuelva `X | null`, no solo este.
   */
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    /**
     * UN 401 SIGNIFICA «TU SESIÓN SE VENCIÓ», SALVO CUANDO ESTÁS ENTRANDO.
     *
     * El 401 de escribir mal la contraseña también es un 401, y ahí no hay
     * ninguna sesión que cerrar. Se distingue por la petición y no por «si
     * mandamos token»: alguien que YA ESTÁ DENTRO y abre la pantalla de
     * entrar para cambiar de cuenta manda token igual, y con esa regla un
     * error de tecleo le cerraba la sesión que tenía abierta.
     */
    /*
     * EL 401 YA NO CIERRA LA SESIÓN ACÁ.
     *
     * Ahora lo decide `request`, que primero intenta renovar: cerrar en este
     * punto sacaría a la persona en el momento exacto en que la sesión se
     * podía salvar sola, que es lo que este cambio vino a evitar.
     *
     * SALVO EN EL REINTENTO. Si ya se renovó y el servidor SIGUE diciendo 401,
     * no queda nada por probar y esto es una sesión muerta de verdad.
     */
    if (response.status === 401 && options.authenticating !== true && reintentando) {
      onUnauthorized?.();
    }
    if (isApiErrorBody(parsed)) {
      throw new ApiError(parsed.code, response.status, parsed.message, parsed.fieldErrors);
    }
    throw new ApiError('UNKNOWN', response.status, `HTTP ${response.status}`);
  }

  return parsed as TResponse;
}

/**
 * Los campos de texto de una comunidad, más el logo si lo hay.
 *
 * Las categorías van como JSON dentro de un campo de texto porque esto es
 * `multipart/form-data` —lleva el logo— y en multipart todo valor es texto:
 * no hay forma de mandar un arreglo de objetos. Crear el club en una petición
 * y las categorías en otra dejaría, si la segunda falla, un club en el que
 * nadie se puede inscribir.
 */
function communityForm(
  fields: CreateCommunityFields,
  categories: readonly CommunityCategory[],
  logo: File | null,
): FormData {
  const form = new FormData();
  form.set('name', fields.name);
  form.set('description', fields.description);
  form.set('locationText', fields.locationText);
  form.set('whatsappGroupUrl', fields.whatsappGroupUrl ?? '');
  form.set('categories', JSON.stringify(categories));
  if (logo !== null) form.set(COMMUNITY_LOGO_FIELD, logo);
  return form;
}

/**
 * LOS TRES CAMPOS QUE NOMBRAN UN CUADRO, listos para el cuerpo de una
 * petición. Ni uno más.
 *
 * NI UNO MENOS: mandar el nivel y el sexo sin la disciplina es lo que hacían
 * `drawTournament` y `reseedTournament`, y el servidor contestaba «Elegí
 * singles o dobles» dentro de una caja roja en medio del cuadro. El nombre de
 * la disciplina no le dice nada a quien solo quería tirar el cuadro del A
 * masculino.
 *
 * Y NI UNO MÁS: quien llama suele tener un `TournamentCategory`, que además
 * trae `startedAt` y `format`. Con un `...categoria` esos dos se iban en la
 * petición — estado del torneo viajando de vuelta al servidor como si lo
 * decidiera el cliente.
 *
 * Recibe el objeto, así que el día que un cuadro tenga un campo más entra acá
 * y en ningún otro lado.
 */
function cuadro(categoria: CommunityCategory): {
  level: CategoryLevel;
  sex: CategorySex;
  discipline: CategoryDiscipline;
} {
  return {
    level: categoria.level,
    sex: categoria.sex,
    discipline: categoria.discipline,
  };
}

/**
 * De un filtro a «?a=1&b=2», sin los vacíos.
 *
 * Los `undefined`, los nulos y las cadenas vacías se caen: el servidor los
 * trata como «no filtres» de todos modos, pero mandarlos deja una URL llena de
 * parámetros mudos que estorban al leer los registros.
 */
function queryString(query: object): string {
  const params = new URLSearchParams();
  for (const [clave, valor] of Object.entries(query)) {
    if (valor === undefined || valor === null || valor === '') continue;
    params.set(clave, String(valor));
  }
  const texto = params.toString();
  return texto === '' ? '' : `?${texto}`;
}

/**
 * QUÉ PARTE DEL TABLÓN SE ESTÁ MIRANDO. Todo opcional: sin nada, el tablón
 * entero. Las fechas van como «2026-09-20» —un día, no un instante— porque un
 * aviso tiene día y no hora.
 */
export interface PostFilter {
  levels?: readonly CategoryLevel[];
  from?: string;
  to?: string;
}

export const api = {
  // Registro y login devuelven lo mismo —una sesión— porque registrarse ya
  // deja adentro: no hay confirmación de correo de por medio.
  // LAS CUATRO VAN CON `authenticating`: su 401 es «esa contraseña no es» y
  // no «tu sesión se venció». Ver el comentario en `request`.
  register(body: RegisterRequest): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/register', {
      method: 'POST',
      body,
      authenticating: true,
    });
  },
  login(body: LoginRequest): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/login', { method: 'POST', body, authenticating: true });
  },
  requestPasswordReset(body: RequestPasswordResetRequest): Promise<void> {
    return request<void>('/auth/request-password-reset', {
      method: 'POST',
      body,
      authenticating: true,
    });
  },
  resetPassword(body: ResetPasswordRequest): Promise<void> {
    return request<void>('/auth/reset-password', {
      method: 'POST',
      body,
      authenticating: true,
    });
  },

  /**
   * SALIR: BORRA LA COOKIE DEL LADO DEL SERVIDOR.
   *
   * La cookie es `HttpOnly`, así que la pantalla NO puede borrarla sola:
   * olvidar el token de `localStorage` dejaría a la persona adentro en cuanto
   * recargue. `authenticating: true` porque su 401 —una sesión que el servidor
   * ya no acepta— no tiene que disparar el logout automático: salir es
   * justamente lo que se está haciendo.
   */
  logout(refreshToken?: string): Promise<void> {
    // LA LLAVE VIAJA SI LA HAY, para que la sesión muera también del lado del
    // servidor y no solo en este teléfono. Si no hay, salir igual funciona.
    const body = refreshToken === undefined ? {} : { refreshToken };
    return request<void>('/auth/logout', { method: 'POST', body, authenticating: true });
  },

  /**
   * RENOVAR LA SESIÓN. `authenticating: true` y es importante:
   *
   * su propio 401 —la llave ya no sirve— NO tiene que disparar otra
   * renovación. Sin esa marca, una llave muerta produciría un intento de
   * renovar la renovación, y así hasta el bucle.
   */
  refreshSession(body: RefreshRequest): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/refresh', { method: 'POST', body, authenticating: true });
  },

  /** Quién soy según el servidor, no según lo que quedó guardado al entrar. */
  me(): Promise<AuthUser> {
    return request<AuthUser>('/profile', { method: 'GET' });
  },
  /** El perfil COMPLETO, que es otra cosa: lo que la pantalla de edición necesita. */
  myProfile(): Promise<PlayerProfile> {
    return request<PlayerProfile>('/profile/full', { method: 'GET' });
  },
  updateProfile(body: UpdateProfileRequest): Promise<PlayerProfile> {
    return request<PlayerProfile>('/profile', { method: 'PUT', body });
  },
  changePassword(body: ChangePasswordRequest): Promise<void> {
    return request<void>('/profile/password', { method: 'POST', body });
  },
  uploadPhoto(photo: File): Promise<AuthUser> {
    const form = new FormData();
    form.set(AVATAR_FIELD, photo);
    return request<AuthUser>('/profile/photo', { method: 'POST', body: form });
  },

  listCommunities(): Promise<Community[]> {
    return request<Community[]>('/communities', { method: 'GET' });
  },
  listMyCommunities(): Promise<Community[]> {
    return request<Community[]>('/communities/mine', { method: 'GET' });
  },
  findCommunity(id: string): Promise<Community> {
    return request<Community>(`/communities/${id}`, { method: 'GET' });
  },
  createCommunity(
    fields: CreateCommunityFields,
    categories: readonly CommunityCategory[],
    logo: File | null,
  ): Promise<Community> {
    return request<Community>('/communities', {
      method: 'POST',
      body: communityForm(fields, categories, logo),
    });
  },
  setCommunityCategories(id: string, categories: readonly CommunityCategory[]): Promise<Community> {
    return request<Community>(`/communities/${id}/categories`, { method: 'PUT', body: { categories } });
  },

  /**
   * QUIÉN ADMINISTRA EL CLUB. Lo ve cualquiera que pueda ver el club:
   * administrar es un cargo, no un dato personal. No trae teléfono — se llega
   * a la persona por su ficha, que es donde vive la regla de contacto.
   */
  listCommunityAdmins(id: string): Promise<CommunityAdmin[]> {
    return request<CommunityAdmin[]>(`/communities/${id}/admins`, { method: 'GET' });
  },

  listMembers(id: string): Promise<CommunityMember[]> {
    return request<CommunityMember[]>(`/communities/${id}/members`, { method: 'GET' });
  },
  searchPlayers(
    id: string,
    query: string,
    /**
     * Quién puede jugar ESTA categoría. `null` = no filtrar.
     *
     * Va entero o no va: media categoría no existe, y el servidor descarta el
     * filtro si le llega solo una mitad.
     */
    canPlay: { level: CategoryLevel; sex: Sex } | null,
    signal?: AbortSignal,
  ): Promise<PlayerSearchPage> {
    // `encodeURIComponent` no es opcional: sin él, un apodo con «&» o «#»
    // parte la URL y la búsqueda devuelve cualquier cosa.
    //
    // `signal` es lo que permite CANCELAR una búsqueda cuando la persona sigue
    // escribiendo: sin eso, la respuesta de «jos» puede llegar después de la
    // de «jose» y pisarla.
    // `signal` se agrega solo si vino: con `exactOptionalPropertyTypes`,
    // pasar `signal: undefined` no es lo mismo que no pasarlo.
    const options: RequestOptions = { method: 'GET' };
    if (signal !== undefined) options.signal = signal;
    const partes = [`q=${encodeURIComponent(query)}`];
    if (canPlay !== null) {
      partes.push(`level=${encodeURIComponent(canPlay.level)}`);
      partes.push(`sex=${encodeURIComponent(canPlay.sex)}`);
    }

    return request<PlayerSearchPage>(
      `/communities/${id}/players?${partes.join('&')}`,
      options,
    );
  },
  addMember(id: string, body: AddCommunityMemberRequest): Promise<CommunityMember> {
    return request<CommunityMember>(`/communities/${id}/members`, { method: 'POST', body });
  },
  updateMember(id: string, userId: string, body: UpdateCommunityMemberRequest): Promise<CommunityMember> {
    return request<CommunityMember>(`/communities/${id}/members/${userId}`, { method: 'PUT', body });
  },
  removeMember(id: string, userId: string): Promise<void> {
    return request<void>(`/communities/${id}/members/${userId}`, { method: 'DELETE' });
  },

  /**
   * IRSE DEL CLUB, uno mismo.
   *
   * Sin `userId` a propósito: es una ruta distinta de `removeMember`, que es
   * la de quien administra. Una es irse, la otra es echar, y con la misma
   * ruta para las dos, relajar el permiso de una relajaría el de la otra.
   */
  leaveCommunity(id: string): Promise<void> {
    return request<void>(`/communities/${id}/membership`, { method: 'DELETE' });
  },

  /** Pedir entrar a un club. La categoría es obligatoria. */
  /**
   * PEDIR ENTRAR. `puntos` es opcional y APAGA el auto-aceptar del club: el
   * interruptor dice «confío en quién entra», no «confío en cuántos puntos se
   * pone cada quien».
   */
  requestJoin(
    id: string,
    level: CategoryLevel,
    puntos?: { points: number; note: string },
  ): Promise<Community> {
    return request<Community>(`/communities/${id}/join`, {
      method: 'POST',
      body:
        puntos === undefined
          ? { level }
          : {
              level,
              requestedPoints: puntos.points,
              // Vacía no se manda: una nota en blanco y sin nota son lo mismo.
              ...(puntos.note.trim() === '' ? {} : { requestedPointsNote: puntos.note.trim() }),
            },
    });
  },
  joinRequests(id: string): Promise<JoinRequest[]> {
    return request<JoinRequest[]>(`/communities/${id}/join-requests`, { method: 'GET' });
  },
  /**
   * LOS PEDIDOS DE SEGUNDA TABLA. Lista aparte de `joinRequests` porque se
   * contestan con `reviewRankingRequest`, no con `approveJoin`/`rejectJoin`.
   */
  rankingRequests(id: string): Promise<JoinRequest[]> {
    return request<JoinRequest[]>(`/communities/${id}/ranking-requests`, { method: 'GET' });
  },
  /**
   * LOS PEDIDOS DE PUNTOS de quien YA ESTÁ ADENTRO. Tercera lista, y tercer
   * juego de botones.
   *
   * POR QUÉ NO ES UN CASO DE `joinRequests`. Ahí estaban, y ahí se
   * contestaban con los botones de ingreso: `rejectJoin` es un `DELETE` de la
   * membresía, así que el botón de «no le doy esos puntos» sacaba del club a
   * un socio. Lo encontró Javier en producción.
   */
  pointRequests(id: string): Promise<JoinRequest[]> {
    return request<JoinRequest[]>(`/communities/${id}/point-requests`, { method: 'GET' });
  },
  /**
   * RESPONDER UN PEDIDO DE PUNTOS. `points` es lo que se concede: ausente
   * concede lo pedido, cero es decir que no. NO HAY «rechazar» con `DELETE`
   * acá, a propósito: un `DELETE` en esta pantalla es el botón que borraba
   * membresías.
   */
  reviewPointRequest(id: string, userId: string, points?: number): Promise<void> {
    return request<void>(`/communities/${id}/point-requests/${userId}`, {
      method: 'POST',
      body: points === undefined ? {} : { points },
    });
  },
  /**
   * APROBAR UNA SOLICITUD. `points` es LO QUE SE CONCEDE de lo que pidió:
   * ausente concede lo pedido, cero lo deja entrar sin puntos.
   */
  approveJoin(id: string, userId: string, points?: number): Promise<CommunityMember> {
    return request<CommunityMember>(`/communities/${id}/join-requests/${userId}/approve`, {
      method: 'POST',
      body: points === undefined ? {} : { points },
    });
  },
  rejectJoin(id: string, userId: string): Promise<void> {
    return request<void>(`/communities/${id}/join-requests/${userId}`, { method: 'DELETE' });
  },
  /**
   * QUITAR el permiso de administrar.
   *
   * DARLO YA NO PASA POR ACÁ: se invita y la persona acepta por correo. Ver
   * `inviteAdmin`. Mandar `ADMIN` desde acá lo rechaza el servidor.
   */
  setMemberRole(id: string, userId: string, role: string): Promise<CommunityMember> {
    return request<CommunityMember>(`/communities/${id}/members/${userId}/role`, {
      method: 'PUT',
      body: { role },
    });
  },

  // --- El tablón de «busco partido» ---
  //
  // EL TELÉFONO NO VIENE CON LA LISTA, y por eso `contactoDeAviso` es una
  // llamada aparte: el tablón lo ve cualquiera con cuenta, así que si el
  // número viajara en `listPosts`, abrir la pantalla sería descargarse todos
  // los WhatsApp de la aplicación. Se pide UNO, al tocar «escribirle».
  /**
   * LOS CUATRO CONTADORES DEL INICIO, en una sola consulta.
   *
   * LAS FECHAS LAS PONE EL TELÉFONO: es quien recuerda cuándo se miró cada
   * pantalla. Ver `lib/visitas.ts`.
   *
   * LO QUE NO SE MIRÓ NUNCA NO VIAJA, y no es cosmética: el servidor lee la
   * ausencia como «nunca miró» y contesta cero SIN ir a la base. Mandar una
   * fecha inventada lo obligaría a contar para devolver un número que igual no
   * se va a dibujar.
   */
  novelties(visitas: Partial<Record<string, string>>): Promise<Novelties> {
    const q = new URLSearchParams();
    for (const [pantalla, cuándo] of Object.entries(visitas)) {
      if (cuándo !== undefined && cuándo !== '') q.set(pantalla, cuándo);
    }
    const cola = q.toString();
    return request<Novelties>(`/wall/novelties${cola === '' ? '' : `?${cola}`}`, {
      method: 'GET',
    });
  },

  listPosts(page = 0, filtro: PostFilter = {}): Promise<ChallengePostPage> {
    /*
     * LO QUE NO SE PIDIÓ NO VIAJA. Sin esto, el tablón sin filtrar pediría
     * `?levels=&from=&to=`, que es una URL distinta para la misma consulta:
     * rompe el caché del navegador y hace ilegible el registro del servidor.
     */
    const q = new URLSearchParams();
    if (page > 0) q.set('page', String(page));
    if (filtro.levels !== undefined && filtro.levels.length > 0) {
      q.set('levels', filtro.levels.join(','));
    }
    if (filtro.from !== undefined) q.set('from', filtro.from);
    if (filtro.to !== undefined) q.set('to', filtro.to);
    const cola = q.toString();
    return request<ChallengePostPage>(`/challenge-posts${cola === '' ? '' : `?${cola}`}`, {
      method: 'GET',
    });
  },
  createPost(body: SaveChallengePostRequest): Promise<ChallengePost> {
    return request<ChallengePost>('/challenge-posts', { method: 'POST', body });
  },
  updatePost(id: string, body: SaveChallengePostRequest): Promise<ChallengePost> {
    return request<ChallengePost>(`/challenge-posts/${id}`, { method: 'PUT', body });
  },
  /**
   * «Ya conseguí partido», y lo contrario.
   *
   * NO ES BORRAR: el aviso se queda una semana como señal de que esa persona
   * juega, con el botón de escribirle puesto. Ver la pantalla.
   */
  setPostSettled(id: string, settled: boolean): Promise<ChallengePost> {
    return request<ChallengePost>(`/challenge-posts/${id}/settled`, {
      method: 'PUT',
      body: { settled },
    });
  },
  removePost(id: string): Promise<void> {
    return request<void>(`/challenge-posts/${id}`, { method: 'DELETE' });
  },
  /** El WhatsApp de quien publicó ESE aviso. Se pide al tocar el botón. */
  contactoDeAviso(id: string): Promise<ChallengePostContact> {
    return request<ChallengePostContact>(`/challenge-posts/${id}/whatsapp`, { method: 'GET' });
  },

  // --- Canchas ---
  //
  // Las carga y las corrige cualquiera: no hay endpoint de permisos porque no
  // hay permisos. Tampoco hay `DELETE`, y es a propósito: ver el controlador.
  listCourts(query: CourtQuery = {}): Promise<CourtPage> {
    const params = new URLSearchParams();
    if (query.provinceCode) params.set('provinceCode', query.provinceCode);
    if (query.cantonCode) params.set('cantonCode', query.cantonCode);
    if (query.text) params.set('text', query.text);
    if (query.page) params.set('page', String(query.page));
    const cola = params.toString();
    return request<CourtPage>(`/courts${cola === '' ? '' : `?${cola}`}`, { method: 'GET' });
  },
  findCourt(id: string): Promise<Court> {
    return request<Court>(`/courts/${id}`, { method: 'GET' });
  },
  createCourt(body: SaveCourtRequest): Promise<Court> {
    return request<Court>('/courts', { method: 'POST', body });
  },
  updateCourt(id: string, body: SaveCourtRequest): Promise<Court> {
    return request<Court>(`/courts/${id}`, { method: 'PUT', body });
  },

  /** Invita a alguien a administrar el club: le manda el correo con el enlace. */
  inviteAdmin(id: string, userId: string): Promise<AdminInvitation> {
    return request<AdminInvitation>(`/communities/${id}/admin-invitations`, {
      method: 'POST',
      body: { userId },
    });
  },
  /** Las invitaciones del club que esperan respuesta. */
  listAdminInvitations(id: string): Promise<AdminInvitation[]> {
    return request<AdminInvitation[]>(`/communities/${id}/admin-invitations`, { method: 'GET' });
  },
  /** Cancela una que se mandó por error. */
  revokeAdminInvitation(id: string, inviteId: string): Promise<void> {
    return request<void>(`/communities/${id}/admin-invitations/${inviteId}`, { method: 'DELETE' });
  },

  /**
   * LO QUE HAY DETRÁS DE UN ENLACE DE INVITACIÓN, antes de decidir.
   *
   * Pide sesión aunque venga de un correo: el permiso se le da a una cuenta, y
   * sin sesión no hay a quién dárselo. La pantalla se guarda el token mientras
   * la persona entra.
   */
  previewAdminInvitation(token: string): Promise<AdminInvitationPreview> {
    return request<AdminInvitationPreview>(`/admin-invitations?token=${encodeURIComponent(token)}`, {
      method: 'GET',
    });
  },
  acceptAdminInvitation(token: string): Promise<AcceptAdminInvitationResponse> {
    return request<AcceptAdminInvitationResponse>('/admin-invitations/accept', {
      method: 'POST',
      body: { token },
    });
  },
  declineAdminInvitation(token: string): Promise<void> {
    return request<void>('/admin-invitations/decline', { method: 'POST', body: { token } });
  },

  /**
   * Los DOS interruptores de auto-aceptar, siempre juntos: el servidor los
   * pide así para no tener que suponer el que falte.
   */
  setAutoApprove(
    id: string,
    autoApproveMembers: boolean,
    autoApproveWithPoints: boolean,
  ): Promise<Community> {
    return request<Community>(`/communities/${id}/auto-approve`, {
      method: 'PUT',
      body: { autoApproveMembers, autoApproveWithPoints },
    });
  },

  /** Los dos interruptores de partido. Se manda solo el que cambió. */
  setMatchSettings(id: string, values: SetMatchSettingsRequest): Promise<Community> {
    return request<Community>(`/communities/${id}/match-settings`, { method: 'PUT', body: values });
  },

  /**
   * LOS FRENOS DE RETOS DEL CLUB. Se manda solo lo que cambió.
   *
   * `positionLimit: null` EXPLÍCITO borra el límite; no mandarlo lo deja como
   * estaba. Son dos cosas distintas y por eso el tipo las distingue.
   */
  setChallengeSettings(id: string, values: SetChallengeSettingsRequest): Promise<Community> {
    return request<Community>(`/communities/${id}/challenge-settings`, {
      method: 'PUT',
      body: values,
    });
  },

  /** El tema, guardado en la cuenta y no solo en este navegador. */
  /**
   * CÓMO ME ENCUENTRAN PARA JUGAR: el nivel declarado, si acepto retos y si
   * los avisos también van por correo.
   *
   * RUTA PROPIA Y NO PARTE DE GUARDAR EL PERFIL, por lo mismo que el tema: se
   * eligen en «Más», y guardar el formulario del perfil con un valor viejo
   * cargado no puede pisarlas.
   */
  setPreferences(body: SetPreferencesRequest): Promise<PlayerProfile> {
    return request<PlayerProfile>('/profile/preferences', { method: 'PUT', body });
  },

  /**
   * A QUIÉN LLAMAR SI ME PASA ALGO EN LA CANCHA.
   *
   * SE MANDA TODO JUNTO Y REEMPLAZA LO QUE HABÍA, incluida la lista vacía:
   * borrar los contactos tiene que ser posible, y mandar cero es la única
   * forma de decirlo. Ruta propia por lo mismo que las preferencias: guardar
   * el formulario del perfil no puede pisar esto.
   */
  setEmergency(body: SetEmergencyRequest): Promise<PlayerProfile> {
    return request<PlayerProfile>('/profile/emergency', { method: 'PUT', body });
  },

  setTheme(theme: Theme): Promise<PlayerProfile> {
    return request<PlayerProfile>('/profile/theme', { method: 'PUT', body: { theme } });
  },

  /** El club que abre la pestaña de Torneos. `null` lo quita. */
  setDefaultCommunity(communityId: string | null): Promise<PlayerProfile> {
    return request<PlayerProfile>('/profile/default-community', {
      method: 'PUT',
      body: { communityId },
    });
  },

  /** «Quiero pasar a B». Queda esperando: la categoría no se mueve hasta que el club responda. */
  requestCategoryChange(id: string, level: CategoryLevel): Promise<Community> {
    return request<Community>(`/communities/${id}/category-request`, {
      method: 'PUT',
      body: { level },
    });
  },
  /**
   * PEDIR ENTRAR A OTRA TABLA. No es cambiar de categoría: es una más.
   *
   * MANDA EL SEXO, y ahí está la diferencia con `requestCategoryChange`, que
   * lo deduce del perfil. Una mujer puede pedir el ranking masculino.
   */
  askForRanking(id: string, body: AskForRankingRequest): Promise<Community> {
    return request<Community>(`/communities/${id}/rankings`, { method: 'POST', body });
  },

  cancelRankingRequest(id: string, body: AskForRankingRequest): Promise<Community> {
    return request<Community>(`/communities/${id}/rankings/cancel`, { method: 'POST', body });
  },

  /** La respuesta del club a un pedido de tabla, y la inscripción directa. */
  reviewRankingRequest(
    id: string,
    userId: string,
    body: ReviewRankingRequest,
  ): Promise<CommunityMember> {
    return request<CommunityMember>(`/communities/${id}/members/${userId}/rankings`, {
      method: 'POST',
      body,
    });
  },

  cancelCategoryChange(id: string): Promise<Community> {
    return request<Community>(`/communities/${id}/category-request`, { method: 'DELETE' });
  },
  /** Responderle a alguien: que sí o que no. Solo quien administra. */
  reviewCategoryChange(id: string, userId: string, approve: boolean): Promise<CommunityMember> {
    return request<CommunityMember>(`/communities/${id}/members/${userId}/category-request`, {
      method: 'POST',
      body: { approve },
    });
  },

  /** La ficha del club: descripción, lugar y grupo. El nombre no se toca acá. */
  setCommunityDetails(id: string, details: UpdateCommunityDetailsRequest): Promise<Community> {
    return request<Community>(`/communities/${id}/details`, { method: 'PUT', body: details });
  },

  setCommunityRules(id: string, tournamentRules: string): Promise<Community> {
    return request<Community>(`/communities/${id}/rules`, { method: 'PUT', body: { tournamentRules } });
  },

  /**
   * LAS CUENTAS GUARDADAS DEL CLUB: el molde del que sale cada torneo nuevo.
   *
   * Las mismas rutas que las del torneo, una a una y no la lista entera: con
   * un `PUT` completo, dos personas administrando a la vez se borrarían las
   * cuentas de la otra.
   */

  // --- Torneos ---

  listTournaments(communityId: string): Promise<Tournament[]> {
    return request<Tournament[]>(`/communities/${communityId}/tournaments`, { method: 'GET' });
  },
  createTournament(communityId: string, body: CreateTournamentFields): Promise<Tournament> {
    return request<Tournament>(`/communities/${communityId}/tournaments`, { method: 'POST', body });
  },
  findTournament(id: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}`, { method: 'GET' });
  },
  /**
   * Corregir la ficha: nombre, descripción, fecha y precios.
   *
   * VA LA FICHA ENTERA, no solo lo que cambió: un campo ausente se leería
   * como «borralo» tan bien como «dejalo igual», y el formulario los tiene
   * todos a la vista igual. Las CATEGORÍAS no van acá — tienen su propia
   * puerta, que sabe que un cuadro sorteado no se puede sacar.
   */
  updateTournament(id: string, body: UpdateTournamentRequest): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}`, { method: 'PUT', body });
  },
  /** Los torneos que ya terminaron, de todos mis clubes. */
  myFinishedTournaments(): Promise<TournamentHistoryRow[]> {
    return request<TournamentHistoryRow[]>('/tournaments/mine/history', { method: 'GET' });
  },

  /** Mis próximos partidos, de todos mis torneos a la vez. */
  myUpcomingMatches(): Promise<UpcomingMatch[]> {
    return request<UpcomingMatch[]>('/tournaments/mine/upcoming', { method: 'GET' });
  },

  /** Dar el torneo por terminado, y deshacerlo. */
  finishTournament(id: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/finish`, { method: 'POST' });
  },
  reopenTournament(id: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/reopen`, { method: 'POST' });
  },
  /** Qué hay en cada uno de mis clubes: en inscripción, en curso, y dónde puedo anotarme. */
  myCommunitiesSummary(): Promise<CommunityTournamentsSummary[]> {
    return request<CommunityTournamentsSummary[]>('/tournaments/mine/summary', { method: 'GET' });
  },
  tournamentBrackets(id: string): Promise<CategoryBracket[]> {
    return request<CategoryBracket[]>(`/tournaments/${id}/brackets`, { method: 'GET' });
  },
  /**
   * RECIBE LA CATEGORÍA ENTERA, no un `level: string` suelto.
   *
   * Así estaba antes y es la clase de firma que no protege nada: con el nivel
   * como texto, agregar la disciplina al servidor NO rompió esta línea —siguió
   * compilando y siguió mandando media categoría—. Es el mismo error que tenía
   * `startTournament`, y lo encontró una prueba y no el compilador.
   */
  addTournamentEntry(
    id: string,
    userId: string,
    categoria: CommunityCategory,
    partnerUserId: string | null,
    paymentMethod?: EntryPaymentMethod,
  ): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/entries`, {
      method: 'POST',
      // Sin medio elegido NO SE MANDA LA CLAVE. Mandar `undefined` la deja
      // fuera del JSON igual, pero decirlo acá hace evidente que «no elegí»
      // no es un valor: el servidor decide qué significa.
      body: {
        userId,
        ...cuadro(categoria),
        ...(partnerUserId === null ? {} : { partnerUserId }),
        ...(paymentMethod === undefined ? {} : { paymentMethod }),
      },
    });
  },

  /**
   * EL CLUB DA POR BUENO EL PAGO, O DICE QUE NO.
   *
   * `note` solo viaja al rechazar: es el motivo, y un motivo colgado de un
   * «sí» se lee como un reparo. El servidor lo descarta igual si llega.
   */
  reviewEntry(
    id: string,
    entryId: string,
    body: ReviewEntryRequest,
  ): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/entries/${entryId}/review`, {
      method: 'POST',
      body,
    });
  },
  /** Ponerlo —o sacarlo— de la vitrina de torneos abiertos. */
  setTournamentPublic(id: string, isPublic: boolean): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/public`, {
      method: 'PUT',
      body: { isPublic },
    });
  },
  /**
   * Los torneos abiertos de TODOS los clubes.
   *
   * Cuelga de `/public` y no de un club porque justamente no es de un club:
   * es la única consulta de torneos que no lleva `communityId`.
   */
  publicTournaments(): Promise<PublicTournamentCard[]> {
    return request<PublicTournamentCard[]>('/public/tournaments', { method: 'GET' });
  },
  /** Inscribir a un INVITADO: alguien que juega y no tiene la aplicación. */
  addGuestEntry(
    id: string,
    guest: {
      name: string;
      sex: Sex;
      whatsappPhone?: string;
      level: CategoryLevel;
      paymentMethod?: EntryPaymentMethod;
    },
  ): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/guest-entries`, {
      method: 'POST',
      body: guest,
    });
  },
  /** Ponerle una cuenta de verdad a una inscripción de invitado. */
  linkGuestEntry(id: string, entryId: string, userId: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/entries/${entryId}/link`, {
      method: 'POST',
      body: { userId },
    });
  },
  /**
   * SALE UNO Y ENTRA OTRO en la misma plaza del cuadro.
   *
   * `PUT` y no `POST`, al revés que vincular: la plaza ya existe y hacerlo dos
   * veces con la misma persona deja lo mismo que hacerlo una.
   */
  replaceEntryPlayer(id: string, entryId: string, userId: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/entries/${entryId}/player`, {
      method: 'PUT',
      body: { userId },
    });
  },
  removeTournamentEntry(id: string, entryId: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/entries/${entryId}`, { method: 'DELETE' });
  },
  /**
   * Sortear: los cuatro primeros del ranking a las esquinas, el resto al azar.
   *
   * `POST` porque NO es idempotente a propósito: cada llamada echa el azar de
   * nuevo, que es lo que quiere quien aprieta «volver a sortear».
   */
  drawTournament(id: string, categoria: CommunityCategory): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/draw`, {
      method: 'POST',
      body: cuadro(categoria),
    });
  },
  reseedTournament(
    id: string,
    categoria: CommunityCategory,
    entryIdsInOrder: readonly string[],
  ): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/seeding`, {
      method: 'PUT',
      body: { ...cuadro(categoria), entryIdsInOrder },
    });
  },
  tournamentMatches(id: string): Promise<Match[]> {
    return request<Match[]>(`/tournaments/${id}/matches`, { method: 'GET' });
  },
  /**
   * Anota o borra el marcador de un partido.
   *
   * `PUT` a propósito: anotar dos veces el mismo marcador deja todo igual.
   * Quien está en la cancha con el teléfono aprieta dos veces más seguido de
   * lo que uno cree. Mandar `[]` borra el resultado.
   */
  /**
   * BORRAR EL MARCADOR DE UN PARTIDO DE TORNEO.
   *
   * RUTA PROPIA Y NO `saveMatchScore(..., [])`, que es como se hacía: por ahí
   * borraba cualquiera que pudiera ANOTAR, y en un club que deja anotar a los
   * jugadores eso permitía borrar el avance de otros por el cuadro. Ver
   * `canClearScore`.
   */
  /**
   * BORRAR EL TORNEO ENTERO. No devuelve nada: ya no existe.
   *
   * Se puede con gente inscrita, pero NO si ya se jugó algo — eso lo decide el
   * servidor y contesta `TOURNAMENT_HAS_PLAYED_MATCHES`.
   */
  deleteTournament(id: string): Promise<void> {
    return request<void>(`/tournaments/${id}`, { method: 'DELETE' });
  },

  clearMatchScore(tournamentId: string, matchId: string): Promise<Match[]> {
    return request<Match[]>(`/tournaments/${tournamentId}/matches/${matchId}/result`, {
      method: 'DELETE',
    });
  },
  saveMatchScore(
    id: string,
    matchId: string,
    score: MatchScore,
    noShowEntryId: string | null = null,
  ): Promise<Match[]> {
    return request<Match[]>(`/tournaments/${id}/matches/${matchId}`, {
      method: 'PUT',
      // El W.O. viaja con el marcador y no por otro camino: las dos cosas
      // contestan cómo terminó este partido, y las dos hacen avanzar a
      // alguien. Con dos endpoints habría dos lugares que mueven el cuadro.
      body: { score, noShowEntryId },
    });
  },

  /**
   * El ranking de UNA tabla del club.
   *
   * `sex` acá es el DE LA PERSONA y no el del cuadro: una tabla del ranking es
   * de gente, y en un dobles mixto cada quien suma en la suya. Por eso esta no
   * recibe una categoría entera como las de arriba — no existe la tabla del
   * mixto—, y por eso los dos argumentos van con su tipo puesto.
   */
  ranking(
    communityId: string,
    level: CategoryLevel,
    sex: Sex,
    discipline: CategoryDiscipline,
  ): Promise<RankingRow[]> {
    const partes = [
      `level=${encodeURIComponent(level)}`,
      `sex=${encodeURIComponent(sex)}`,
      `discipline=${encodeURIComponent(discipline)}`,
    ];
    return request<RankingRow[]>(`/communities/${communityId}/ranking?${partes.join('&')}`, {
      method: 'GET',
    });
  },
  /** Los puntos de cada persona del club, para ordenar el listado de jugadores. */
  communityPoints(communityId: string): Promise<PlayerPoints[]> {
    return request<PlayerPoints[]>(`/communities/${communityId}/points`, { method: 'GET' });
  },
  /** Quién jugó por encima de su categoría. Solo para quien administra el club. */
  playedUp(communityId: string): Promise<PlayedUpNotice[]> {
    return request<PlayedUpNotice[]>(`/communities/${communityId}/played-up`, { method: 'GET' });
  },

  /**
   * Tira UN cuadro, no el torneo entero. Ver `start` en el servicio.
   *
   * RECIBE LA CATEGORÍA ENTERA Y TIPADA. Antes eran dos `string` sueltos, y
   * por eso fue el único lugar de toda la aplicación donde agregar la
   * disciplina NO rompió la compilación: mandaba un cuadro a medio identificar
   * y el compilador no tenía cómo notarlo. Lo encontró una prueba.
   */
  startTournament(id: string, categoria: DrawRequest): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/start`, {
      method: 'POST',
      body: categoria,
    });
  },
  /** La foto de un partido: la del final, o el marcador en la pizarra. */
  setMatchPhoto(id: string, matchId: string, photo: File): Promise<Match[]> {
    const form = new FormData();
    form.set(MATCH_PHOTO_FIELD, photo);
    return request<Match[]>(`/tournaments/${id}/matches/${matchId}/photo`, {
      method: 'POST',
      body: form,
    });
  },
  /**
   * LA FOTO DE UN RETO. El mismo campo que la de un partido de cuadro: es la
   * misma idea —la foto de este partido— y dos nombres para lo mismo son el
   * lugar donde un día uno cambia y el otro no.
   */
  setChallengePhoto(id: string, photo: File): Promise<Challenge> {
    const form = new FormData();
    form.set(MATCH_PHOTO_FIELD, photo);
    return request<Challenge>(`/challenges/${id}/photo`, { method: 'POST', body: form });
  },
  removeChallengePhoto(id: string): Promise<Challenge> {
    return request<Challenge>(`/challenges/${id}/photo`, { method: 'DELETE' });
  },

  removeMatchPhoto(id: string, matchId: string): Promise<Match[]> {
    return request<Match[]>(`/tournaments/${id}/matches/${matchId}/photo`, { method: 'DELETE' });
  },

  /** Cambiar el logo del club. `POST` y no `PUT`: cada subida guarda un archivo nuevo. */
  setCommunityLogo(id: string, logo: File): Promise<Community> {
    const form = new FormData();
    form.set(COMMUNITY_LOGO_FIELD, logo);
    return request<Community>(`/communities/${id}/logo`, { method: 'POST', body: form });
  },
  /** Quitarlo y quedarse con las iniciales. */
  removeCommunityLogo(id: string): Promise<Community> {
    return request<Community>(`/communities/${id}/logo`, { method: 'DELETE' });
  },
  setTournamentBanner(id: string, banner: File): Promise<Tournament> {
    const form = new FormData();
    form.set(TOURNAMENT_BANNER_FIELD, banner);
    return request<Tournament>(`/tournaments/${id}/banner`, { method: 'POST', body: form });
  },
  removeTournamentBanner(id: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/banner`, { method: 'DELETE' });
  },
  addTournamentSponsor(id: string, name: string, linkUrl: string, image: File): Promise<Tournament> {
    const form = new FormData();
    form.set('name', name);
    form.set('linkUrl', linkUrl);
    form.set(SPONSOR_IMAGE_FIELD, image);
    return request<Tournament>(`/tournaments/${id}/sponsors`, { method: 'POST', body: form });
  },
  removeTournamentSponsor(id: string, sponsorId: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/sponsors/${sponsorId}`, { method: 'DELETE' });
  },

  /**
   * LAS CUENTAS PARA DEPOSITAR. Se agregan y se quitan de a una, como los
   * patrocinadores: con un `PUT` de la lista entera, dos personas editando a
   * la vez se borrarían las cuentas.
   */
  addAccount(id: string, body: AddAccountRequest): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/accounts`, { method: 'POST', body });
  },
  removeAccount(id: string, accountId: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/accounts/${accountId}`, { method: 'DELETE' });
  },

  /**
   * EL REGLAMENTO DE ESTE TORNEO. Mandar vacío lo devuelve al del club.
   *
   * `PUT` y no `POST`: es un campo que se reemplaza entero, no una lista.
   */
  setTournamentRules(id: string, rules: string): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/rules`, { method: 'PUT', body: { rules } });
  },

  /**
   * El comprobante de pago: comentario, imagen, o los dos.
   *
   * `FormData` SIEMPRE, aunque solo vaya el comentario. Un formulario que
   * cambia de tipo de contenido según si hay archivo son dos caminos, y el
   * que se usa menos es el que se rompe sin que nadie note.
   */
  setEntryProof(
    id: string,
    entryId: string,
    proof: { comment?: string; image?: File },
  ): Promise<Tournament> {
    const form = new FormData();
    if (proof.comment !== undefined) form.set('comment', proof.comment);
    if (proof.image !== undefined) form.set(ENTRY_PROOF_FIELD, proof.image);
    return request<Tournament>(`/tournaments/${id}/entries/${entryId}/proof`, {
      method: 'POST',
      body: form,
    });
  },

  /**
   * A quién puedo retar cerca de mí.
   *
   * Los cuatro filtros van en la URL —es una consulta, no crea nada— y por
   * eso hay que convertirlos a texto a mano: en una dirección web no existen
   * ni los números ni los booleanos.
   */
  searchChallengers(
    filtros: ChallengeSearchRequest,
    signal?: AbortSignal,
  ): Promise<ChallengePlayer[]> {
    const query = new URLSearchParams({
      kind: filtros.kind,
      text: filtros.text,
      // El nulo no existe en una URL. Los dos filtros que se pueden apagar
      // viajan como vacío —«sin límite de distancia» y «de cualquier club»—
      // y el DTO los convierte de vuelta. `String(null)` mandaría la palabra
      // «null», que del otro lado no es ni un número ni un vacío.
      km: filtros.km === null ? '' : String(filtros.km),
      communityId: filtros.communityId ?? '',
      sameCategory: String(filtros.sameCategory),
    });
    // `signal` solo si vino: con `exactOptionalPropertyTypes`, pasar
    // `signal: undefined` no es lo mismo que no pasarlo.
    const options: RequestOptions = { method: 'GET' };
    if (signal !== undefined) options.signal = signal;
    return request<ChallengePlayer[]>(`/challenges/players?${query.toString()}`, options);
  },

  /**
   * EL CUADRO DE UN TORNEO, SIN SESIÓN.
   *
   * `authenticating: true` no es para autenticarse: es para que un 401 no
   * dispare la salida al login. Esta pantalla se abre justamente sin sesión,
   * y sacar al login a quien nunca la tuvo es el defecto que se vino a
   * arreglar.
   */
  publicTournament(id: string): Promise<PublicTournament> {
    return request<PublicTournament>(`/public/tournaments/${id}`, {
      method: 'GET',
      authenticating: true,
    });
  },

  // --- El Muro ------------------------------------------------------------

  /** Lo que ya pasó en mis clubes, de más a menos reciente. */
  /**
   * LA ACTIVIDAD, con sus filtros y su página.
   *
   * Los filtros viajan en la dirección: es una lectura, y así la pantalla se
   * puede compartir, recargar y volver atrás sin que el navegador pregunte si
   * reenvía nada. Los vacíos no se mandan — `?level=` es un filtro puesto en
   * nada, que no es lo mismo que sin filtro.
   */
  wallActivity(query: ActivityQuery = {}): Promise<ActivityPage> {
    const params = new URLSearchParams();
    if (query.communityId !== undefined) params.set('communityId', query.communityId);
    if (query.level !== undefined) params.set('level', query.level);
    if (query.sex !== undefined) params.set('sex', query.sex);
    if (query.userIds !== undefined && query.userIds.length > 0) {
      params.set('userIds', query.userIds.join(','));
    }
    if (query.from !== undefined) params.set('from', query.from);
    if (query.to !== undefined) params.set('to', query.to);
    if (query.page !== undefined && query.page > 1) params.set('page', String(query.page));

    const cola = params.toString();
    return request<ActivityPage>(`/wall/activity${cola === '' ? '' : `?${cola}`}`, {
      method: 'GET',
    });
  },

  /**
   * UN SOLO PARTIDO, para su pantalla propia.
   *
   * DOS MÉTODOS Y NO UNO CON EL TIPO AL LADO: son dos direcciones distintas
   * porque son dos tablas distintas, y quien llama ya sabe cuál tiene en la
   * mano. Adivinarlo acá sería adivinarlo en el lugar más lejos del dato.
   */
  matchCard(matchId: string): Promise<ActivityEntry> {
    return request<ActivityEntry>(`/wall/matches/${matchId}/card`, { method: 'GET' });
  },

  challengeCard(challengeId: string): Promise<ActivityEntry> {
    return request<ActivityEntry>(`/wall/challenges/${challengeId}/card`, { method: 'GET' });
  },

  addChallengeComment(challengeId: string, body: string): Promise<MatchComment> {
    return request<MatchComment>(`/wall/challenges/${challengeId}/comments`, {
      method: 'POST',
      body: { body } satisfies AddCommentRequest,
    });
  },

  setChallengeReaction(
    challengeId: string,
    emoji: string,
    on: boolean,
  ): Promise<WallReactionCount[]> {
    return request<WallReactionCount[]>(`/wall/challenges/${challengeId}/reactions`, {
      method: 'PUT',
      body: { emoji, on } satisfies SetReactionRequest,
    });
  },

  /** Lo que se viene, por cuándo se juega. */
  wallUpcoming(): Promise<WallEntry[]> {
    return request<WallEntry[]>('/wall/upcoming', { method: 'GET' });
  },

  addComment(matchId: string, body: string): Promise<MatchComment> {
    return request<MatchComment>(`/wall/matches/${matchId}/comments`, {
      method: 'POST',
      body: { body } satisfies AddCommentRequest,
    });
  },

  removeComment(commentId: string): Promise<void> {
    return request<void>(`/wall/comments/${commentId}`, { method: 'DELETE' });
  },

  /**
   * `PUT` y no dos rutas: el botón es uno y alterna. Con «poner» y «quitar»
   * separadas, la pantalla tiene que acordarse de cuál llamar según un estado
   * que puede estar viejo, y el primer doble toque manda la equivocada.
   */
  setReaction(matchId: string, emoji: string, on: boolean): Promise<WallReactionCount[]> {
    return request<WallReactionCount[]>(`/wall/matches/${matchId}/reactions`, {
      method: 'PUT',
      body: { emoji, on } satisfies SetReactionRequest,
    });
  },

  // ---------------------------------------------------------------------
  // Los retos
  // ---------------------------------------------------------------------

  /** Mis retos: los que mandé y los que me mandaron, más nuevos primero. */
  myChallenges(): Promise<Challenge[]> {
    return request<Challenge[]>('/challenges/mine', { method: 'GET' });
  },

  /**
   * Retar a alguien.
   *
   * `communityId` solo va en los de ranking, y lo elige la pantalla entre los
   * que el servidor dijo que se puede —`PublicPlayer.rankedChallengeIn`—. La
   * regla la vuelve a comprobar el servidor: esto es para no ofrecer un botón
   * que contesta que no.
   */
  createChallenge(body: CreateChallengeRequest): Promise<Challenge> {
    return request<Challenge>('/challenges', { method: 'POST', body });
  },

  /** Contestar un reto que me mandaron: sí o no. */
  answerChallenge(id: string, accept: boolean): Promise<Challenge> {
    return request<Challenge>(`/challenges/${id}/answer`, {
      method: 'POST',
      body: { accept } satisfies AnswerChallengeRequest,
    });
  },

  /** Cancelar uno que mandé, mientras no me contesten. */
  cancelChallenge(id: string): Promise<Challenge> {
    return request<Challenge>(`/challenges/${id}/cancel`, { method: 'POST' });
  },

  /**
   * Anotar quién ganó.
   *
   * EL MARCADOR VA EN EL ORDEN DE QUIEN ANOTA —lo suyo primero—, igual que se
   * muestra. El servidor lo da vuelta si hace falta antes de guardarlo; acá
   * no se invierte nada, porque invertirlo en los dos lados es invertirlo dos
   * veces.
   */
  setChallengeResult(id: string, body: SetChallengeResultRequest): Promise<Challenge> {
    return request<Challenge>(`/challenges/${id}/result`, { method: 'POST', body });
  },

  /** Anotar un amistoso que ya se jugó, sin que exista un reto. */
  reportFriendly(body: ReportFriendlyRequest): Promise<Challenge> {
    return request<Challenge>('/challenges/friendly', { method: 'POST', body });
  },

  /**
   * BORRAR EL MARCADOR: el reto vuelve a estar aceptado y sin resultado.
   *
   * `DELETE` sobre el mismo camino que lo puso, que es lo que HTTP ya
   * significa: el recurso es «el resultado de este reto». Solo la plataforma;
   * la regla vive en el servidor y viaja en `canClearScore`.
   */
  clearChallengeResult(id: string): Promise<Challenge> {
    return request<Challenge>(`/challenges/${id}/result`, { method: 'DELETE' });
  },

  /** El rival da por bueno un amistoso anotado. Recién ahí suma puntos. */
  confirmFriendly(id: string): Promise<Challenge> {
    return request<Challenge>(`/challenges/${id}/confirm`, { method: 'POST' });
  },

  /** El perfil público de otra persona: lo que hace falta para retarla. */
  findPlayer(id: string): Promise<PublicPlayer> {
    return request<PublicPlayer>(`/players/${id}`, { method: 'GET' });
  },

  /**
   * EL CARA A CARA entre quien mira y esa persona.
   *
   * `null` cuando nunca jugaron: no es un error. La pantalla lo dice con
   * palabras, que es distinto de un «0-0» que parece un empate.
   */
  playerRivalry(id: string): Promise<Rivalry | null> {
    return request<Rivalry | null>(`/players/${id}/rivalry`, { method: 'GET' });
  },
  /**
   * EL CARA A CARA ENTRE DOS PERSONAS CUALESQUIERA, sin que quien mira sea
   * ninguna de las dos. Los resultados ya son públicos en el tablón; esto los
   * cuenta. El orden de la URL es el orden de la respuesta: `mine` es del
   * primero.
   */
  rivalryBetween(aId: string, bId: string): Promise<Rivalry | null> {
    return request<Rivalry | null>(`/players/${aId}/rivalry/${bId}`, { method: 'GET' });
  },

  /** Todos los partidos que jugó esa persona, de todos los clubes y años. */
  playerMatches(id: string): Promise<PlayedMatch[]> {
    return request<PlayedMatch[]>(`/players/${id}/matches`, { method: 'GET' });
  },

  /** La ubicación por GPS del perfil propio. */
  setLocation(latitude: number, longitude: number): Promise<PlayerProfile> {
    return request<PlayerProfile>('/profile/location', {
      method: 'PUT',
      body: { latitude, longitude },
    });
  },
  removeLocation(): Promise<PlayerProfile> {
    return request<PlayerProfile>('/profile/location', { method: 'DELETE' });
  },

  /** Cuándo y dónde se juega un partido. Devuelve TODOS: «repetir» cambia varios. */
  setMatchSchedule(
    id: string,
    matchId: string,
    schedule: SetMatchScheduleRequest,
  ): Promise<Match[]> {
    return request<Match[]>(`/tournaments/${id}/matches/${matchId}/schedule`, {
      method: 'PUT',
      body: schedule,
    });
  },

  /**
   * Qué categorías corre el torneo. La lista completa, no un agregado.
   *
   * Ver `SetTournamentCategoriesRequest`: el servidor saca la diferencia con
   * lo que ya hay, y así dos pestañas abiertas no arman una mezcla que
   * ninguna de las dos eligió.
   */
  setTournamentCategories(id: string, categories: readonly CommunityCategory[]): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/categories`, {
      method: 'PUT',
      body: { categories },
    });
  },

  /** Cómo se juega una categoría. Solo antes de tirar ese cuadro. */
  setCategoryFormat(id: string, input: SetCategoryFormatRequest): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/format`, { method: 'PUT', body: input });
  },

  /** La fecha límite de una ronda. `deadline: null` la borra. */
  setRoundDeadline(id: string, input: SetRoundDeadlineRequest): Promise<Tournament> {
    return request<Tournament>(`/tournaments/${id}/round-deadline`, { method: 'PUT', body: input });
  },

  // --- La campanita ---

  notifications(): Promise<NotificationsPage> {
    return request<NotificationsPage>('/notifications', { method: 'GET' });
  },
  markNotificationsRead(): Promise<NotificationsPage> {
    return request<NotificationsPage>('/notifications/read', { method: 'POST', body: {} });
  },

  // --- Comisión (solo PLATFORM_ADMIN) ---

  platformSettings(): Promise<PlatformSettings> {
    return request<PlatformSettings>('/admin/billing/settings', { method: 'GET' });
  },
  savePlatformSettings(body: PlatformSettings): Promise<PlatformSettings> {
    return request<PlatformSettings>('/admin/billing/settings', { method: 'PUT', body });
  },
  billing(): Promise<TournamentBilling[]> {
    return request<TournamentBilling[]>('/admin/billing/tournaments', { method: 'GET' });
  },

  // Administración. Viven en el mismo cliente porque son la misma API; quien
  // no tenga el rol recibe FORBIDDEN, y eso lo decide el servidor.
  listPendingCommunities(): Promise<Community[]> {
    return request<Community[]>('/admin/communities/pending', { method: 'GET' });
  },
  approveCommunity(id: string, body: ReviewCommunityRequest): Promise<Community> {
    return request<Community>(`/admin/communities/${id}/approve`, { method: 'POST', body });
  },
  rejectCommunity(id: string, body: ReviewCommunityRequest): Promise<Community> {
    return request<Community>(`/admin/communities/${id}/reject`, { method: 'POST', body });
  },
  setCommunityTournaments(id: string, body: SetTournamentPermissionRequest): Promise<Community> {
    return request<Community>(`/admin/communities/${id}/tournaments`, { method: 'POST', body });
  },

  // --- Memes ---

  /**
   * EL MURO DE MEMES. De toda la comunidad: no lleva club en ningún lado.
   */
  listMemes(page: number): Promise<MemePage> {
    return request<MemePage>(`/memes?page=${page}`, { method: 'GET' });
  },

  /** La imagen es obligatoria; el pie va en el mismo formulario. */
  createMeme(image: File, caption: string): Promise<Meme> {
    const form = new FormData();
    form.set(MEME_IMAGE_FIELD, image);
    // Vacío NO se manda: un pie en blanco y sin pie son lo mismo, y mandarlo
    // obligaría al servidor a distinguir dos cosas que no se distinguen.
    if (caption.trim() !== '') form.set('caption', caption.trim());
    return request<Meme>('/memes', { method: 'POST', body: form });
  },

  removeMeme(id: string): Promise<void> {
    return request<void>(`/memes/${id}`, { method: 'DELETE' });
  },

  /**
   * Reportar un meme indebido. LA RAZÓN VA SIEMPRE: quien revisa ve una imagen
   * y un nombre, y la misma foto es un meme o un insulto según a quién
   * retrate. El servidor la exige; mandar el cuerpo vacío era lo de antes.
   */
  reportMeme(id: string, note: string): Promise<void> {
    return request<void>(`/memes/${id}/reports`, {
      method: 'POST',
      body: { note: note.trim() },
    });
  },

  commentMeme(id: string, body: string): Promise<Meme> {
    return request<Meme>(`/memes/${id}/comments`, { method: 'POST', body: { body } });
  },

  removeMemeComment(commentId: string): Promise<void> {
    return request<void>(`/memes/comments/${commentId}`, { method: 'DELETE' });
  },

  reactToMeme(id: string, emoji: string, on: boolean): Promise<Meme> {
    return request<Meme>(`/memes/${id}/reactions`, { method: 'PUT', body: { emoji, on } });
  },

  // --- Reportes de jugador dentro del club ---

  /**
   * Reportar a alguien del club. Las fotos van en el mismo formulario.
   *
   * `FormData` siempre, aunque no haya fotos. Un formulario que cambia de tipo
   * de contenido según si adjuntaron algo son dos caminos, y el que se usa
   * menos es el que se rompe sin que nadie note.
   */
  createPlayerReport(
    communityId: string,
    input: CreatePlayerReportRequest,
    photos: readonly File[],
  ): Promise<PlayerReport> {
    const form = new FormData();
    form.set('aboutUserId', input.aboutUserId);
    form.set('happenedOn', input.happenedOn);
    form.set('problem', input.problem);
    // `append` y no `set`: son varias con el mismo nombre de campo, que es
    // exactamente lo que `set` pisaría.
    for (const foto of photos) form.append(PLAYER_REPORT_PHOTO_FIELD, foto);
    return request<PlayerReport>(`/communities/${communityId}/reports`, {
      method: 'POST',
      body: form,
    });
  },

  /** La lista del club. Solo quien lo administra; el servidor lo comprueba. */
  /**
   * UNA PÁGINA DE REPORTES, con su filtro.
   *
   * Los campos vacíos NO se mandan: el servidor los lee como «no filtres»
   * igual, pero una URL con `?aboutUserId=&from=&to=` es ilegible en el
   * registro del servidor justo cuando alguien la está depurando.
   */
  // Quiénes organizan un torneo, y su bitácora interna. Las dos exigen poder
  // organizarlo; el servidor contesta 403 a quien no.
  listTournamentAdmins(tournamentId: string): Promise<TournamentAdmin[]> {
    return request<TournamentAdmin[]>(`/tournaments/${tournamentId}/admins`, { method: 'GET' });
  },
  /**
   * INVITA a alguien a organizar. Ya NO lo suma: le manda el correo con el
   * enlace, y organiza cuando lo acepta. Por eso devuelve la invitación y no
   * el equipo —que no cambió todavía—.
   */
  addTournamentAdmin(tournamentId: string, userId: string): Promise<AdminInvitation> {
    return request<AdminInvitation>(`/tournaments/${tournamentId}/admins`, {
      method: 'POST',
      body: { userId },
    });
  },
  /** Las invitaciones a organizar este torneo que esperan respuesta. */
  listTournamentAdminInvitations(tournamentId: string): Promise<AdminInvitation[]> {
    return request<AdminInvitation[]>(`/tournaments/${tournamentId}/admin-invitations`, {
      method: 'GET',
    });
  },
  /** Cancela una que se mandó por error. */
  revokeTournamentAdminInvitation(tournamentId: string, inviteId: string): Promise<void> {
    return request<void>(`/tournaments/${tournamentId}/admin-invitations/${inviteId}`, {
      method: 'DELETE',
    });
  },
  removeTournamentAdmin(tournamentId: string, userId: string): Promise<TournamentAdmin[]> {
    return request<TournamentAdmin[]>(`/tournaments/${tournamentId}/admins/${userId}`, {
      method: 'DELETE',
    });
  },
  listTournamentNotes(tournamentId: string): Promise<TournamentNote[]> {
    return request<TournamentNote[]>(`/tournaments/${tournamentId}/notes`, { method: 'GET' });
  },
  addTournamentNote(tournamentId: string, body: string): Promise<TournamentNote> {
    return request<TournamentNote>(`/tournaments/${tournamentId}/notes`, {
      method: 'POST',
      body: { body },
    });
  },

  listPlayerReports(communityId: string, query: PlayerReportQuery = {}): Promise<PlayerReportPage> {
    return request<PlayerReportPage>(
      `/communities/${communityId}/reports${queryString(query)}`,
      { method: 'GET' },
    );
  },

  /** Todos los que cumplen el filtro, sin paginar. Para el resumen. */
  listPlayerReportsForSummary(
    communityId: string,
    query: PlayerReportQuery = {},
  ): Promise<PlayerReport[]> {
    return request<PlayerReport[]>(
      `/communities/${communityId}/reports/summary${queryString(query)}`,
      { method: 'GET' },
    );
  },

  /** «Lo miré.» No borra nada: el reporte queda, con quién lo miró y cuándo. */
  markPlayerReportHandled(communityId: string, id: string): Promise<PlayerReport> {
    return request<PlayerReport>(`/communities/${communityId}/reports/${id}/handled`, {
      method: 'POST',
    });
  },

  /**
   * Qué partes de la app están prendidas. SIN SESIÓN: la pantalla de inicio
   * tiene que saber si dibuja la tarjeta de memes antes de saber quién mira.
   */
  platformFeatures(): Promise<PlatformFeatures> {
    return request<PlatformFeatures>('/public/features', { method: 'GET' });
  },

  /*
   * MODERAR MEMES NO VIVE ACÁ. Estaban `listMemeReports`, `dismissMemeReports`
   * y `setMute`, y se mudaron al cliente de la app de administración.
   *
   * No es orden: es que este archivo viaja en el paquete que se baja cualquier
   * jugador. Las llamadas de moderación no se usan desde acá —el servidor las
   * rechaza para quien no administra la plataforma— así que lo único que
   * hacían era contar en el bundle y decirle a quien lo lea dónde están las
   * rutas de moderación.
   */
  /**
   * CORREGIR LOS PUNTOS DE ALGUIEN. Suma o resta, no fija un total: cada
   * corrección es una fila más en el historial, no un número que se pisa.
   */
  adjustPoints(
    communityId: string,
    userId: string,
    points: number,
    note: string,
    /**
     * EN CUÁL DE SUS TABLAS. Obligatorio del lado del servidor cuando la
     * persona compite en dos categorías de ese club: ahí no adivina, pide que
     * se diga. Opcional acá porque con una sola tabla no hay nada que elegir.
     */
    ranking?: { level: CategoryLevel; sex: CategorySex },
  ): Promise<void> {
    return request<void>(`/communities/${communityId}/members/${userId}/adjustments`, {
      method: 'POST',
      body: ranking === undefined ? { points, note } : { points, note, ranking },
    });
  },

  /** El historial de puntos. Lo ve quien administra y la propia persona. */
  pointHistory(communityId: string, userId: string): Promise<PointAdjustment[]> {
    return request<PointAdjustment[]>(
      `/communities/${communityId}/members/${userId}/adjustments`,
      { method: 'GET' },
    );
  },

};
